import { ChevronDown, Target } from "lucide-react";
import { useState } from "react";

import { Skeleton } from "@/components/ui/skeleton";
import { useOmc } from "@/hooks/useOmc";
import { cn } from "@/lib/utils";
import type { Omc, OmcStato } from "@/types/api";

const COLORE: Record<OmcStato, string> = {
  verde: "#059669",
  giallo: "#d97706",
  rosso: "#dc2626",
  in_rilevazione: "#64748b",
  in_collegamento: "#64748b",
};

const ETICHETTA: Record<OmcStato, string> = {
  verde: "A target",
  giallo: "In avvicinamento",
  rosso: "Sotto target",
  in_rilevazione: "In rilevazione",
  in_collegamento: "In collegamento",
};

const VERDE = "#059669";

function fmt(v: number | null, label: string | null, unita: string | null): string {
  if (v !== null && v !== undefined) {
    return `${v.toLocaleString("it-IT")}${unita ? ` ${unita}` : ""}`;
  }
  return label ?? "—";
}

/**
 * Avanzamento OMC (Obiettivi Misurabili Concordati). Card collassabile, chiusa di
 * default: l'header mostra il semaforo a colpo d'occhio; aperta, le card dettaglio
 * con baseline→oggi→target e barra di avanzamento. Dati da `/api/v1/omc`.
 */
export function WidgetOmcProgress() {
  const { data, isLoading } = useOmc();
  const [aperto, setAperto] = useState(false);

  const indicatori = data ?? [];
  const sopraSoglia = indicatori.filter((o) => o.stato === "verde").length;
  const verifica = indicatori.find((o) => o.data_verifica_prossima)?.data_verifica_prossima;
  const verificaTxt = verifica
    ? new Date(verifica).toLocaleDateString("it-IT", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : null;

  return (
    <section className="rounded-xl border bg-card/60">
      <button
        type="button"
        onClick={() => setAperto((v) => !v)}
        aria-expanded={aperto}
        className="flex w-full items-center gap-4 p-5 text-left"
      >
        <Target className="size-5 shrink-0 text-brand-700" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold tracking-tight">
              Avanzamento Obiettivi Concordati (OMC)
            </span>
            {!isLoading && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                {sopraSoglia} / {indicatori.length} sopra soglia
              </span>
            )}
          </div>
          {verificaTxt && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              Verifica trimestrale: {verificaTxt} · soglia inadempimento &lt; 70% su ≥ 2
              indicatori
            </p>
          )}
        </div>
        {/* Semaforo a colpo d'occhio (visibile anche da chiuso). */}
        <div className="ml-auto hidden items-center gap-1.5 sm:flex">
          {indicatori.map((o) => (
            <span
              key={o.codice}
              title={`${o.codice} · ${ETICHETTA[o.stato]}`}
              className="size-2.5 rounded-full"
              style={{ background: COLORE[o.stato] }}
            />
          ))}
        </div>
        <ChevronDown
          className={cn(
            "size-5 shrink-0 text-muted-foreground transition-transform",
            aperto && "rotate-180",
          )}
          aria-hidden="true"
        />
      </button>

      {aperto && (
        <div className="border-t p-5 pt-4">
          {isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : (
            <div className="grid grid-cols-1 gap-x-8 gap-y-6 lg:grid-cols-2">
              {indicatori.map((o) => (
                <OmcCard key={o.codice} omc={o} />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function OmcCard({ omc: o }: { omc: Omc }) {
  const col = COLORE[o.stato];
  return (
    <div>
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-[11px] font-bold tabular-nums text-muted-foreground">
            {o.codice}
          </span>
          <span className="truncate text-sm font-medium">{o.nome}</span>
        </div>
        <span
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium"
          style={{ background: `${col}1a`, color: col }}
        >
          <span className="size-1.5 rounded-full" style={{ background: col }} />
          {ETICHETTA[o.stato]}
        </span>
      </div>

      <div className="mt-2 flex items-center gap-6 text-sm">
        <Valore label="Baseline" testo={fmt(o.baseline, o.baseline_label, o.unita)} />
        <Valore
          label="Oggi"
          testo={fmt(o.valore_corrente, o.valore_corrente_label, o.unita)}
          colore={o.misurato ? col : "#64748b"}
        />
        <Valore
          label="Target 6m"
          testo={fmt(o.target, o.target_label, o.unita)}
          colore={VERDE}
        />
      </div>

      <OmcBarra omc={o} colore={col} />
      {o.nota && <p className="text-xs text-muted-foreground">{o.nota}</p>}
    </div>
  );
}

function Valore({
  label,
  testo,
  colore,
}: {
  label: string;
  testo: string;
  colore?: string;
}) {
  return (
    <div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className="font-semibold tabular-nums" style={colore ? { color: colore } : undefined}>
        {testo}
      </div>
    </div>
  );
}

function OmcBarra({ omc: o, colore }: { omc: Omc; colore: string }) {
  const misurabile =
    o.misurato &&
    o.baseline !== null &&
    o.target !== null &&
    o.valore_corrente !== null;

  if (!misurabile) {
    // Barra tratteggiata neutra per indicatori non ancora misurabili.
    return (
      <div
        className="my-2.5 h-2 rounded-full"
        style={{
          background:
            "repeating-linear-gradient(90deg,#e2e8f0,#e2e8f0 6px,#f1f5f9 6px,#f1f5f9 12px)",
        }}
      />
    );
  }

  const baseline = o.baseline as number;
  const target = o.target as number;
  const corrente = o.valore_corrente as number;
  const lo = Math.min(baseline, target);
  const hi = Math.max(baseline, target);
  const span = hi - lo || 1;
  const pct = (v: number) => Math.max(0, Math.min(100, ((v - lo) / span) * 100));
  const giu = o.verso === "minore_meglio";
  const tgtPos = giu ? 0 : 100;
  const basePos = giu ? 100 : 0;
  const curPos = pct(corrente);
  const fillLeft = Math.min(basePos, curPos);
  const fillRight = Math.max(basePos, curPos);

  return (
    <div className="relative my-2.5 h-2 rounded-full bg-slate-200">
      <div
        className="absolute top-0 h-2 rounded-full"
        style={{ left: `${fillLeft}%`, width: `${fillRight - fillLeft}%`, background: colore }}
      />
      {/* marker target (estremo buono) */}
      <span
        title="target"
        className="absolute size-3.5 -translate-x-1/2 rounded-full border-2 border-white bg-white"
        style={{ top: "-3px", left: `${tgtPos}%`, boxShadow: `0 0 0 2px ${VERDE}` }}
      />
      {/* marker oggi */}
      <span
        title="oggi"
        className="absolute size-3.5 -translate-x-1/2 rounded-full border-2 border-white"
        style={{ top: "-3px", left: `${curPos}%`, background: colore }}
      />
    </div>
  );
}
